/**
 * PDF étiquettes Code 128 — téléchargement local (jsPDF + JsBarcode).
 * Aucun pop-up, aucun CDN.
 */

import { jsPDF } from 'jspdf';
import JsBarcode from 'jsbarcode';
import { BARCODE_PRINT_OPTIONS } from './internalBarcodeLabelPrint.js';

const CLKI_CODE_REGEX = /^CLKI[0-9A-HJKMNP-Z]{8}$/;

/** @typedef {{ title: string, variantLabel?: string, priceLabel?: string|null, code: string }} LabelSpec */

export const A4_LABEL_LAYOUT = {
  pageWidthMm: 210,
  pageHeightMm: 297,
  marginMm: 8,
  cols: 3,
  rows: 7,
  gapMm: 3,
};

export const THERMAL_LABEL_LAYOUT = {
  widthMm: 80,
  heightMm: 40,
};

/**
 * @param {LabelSpec[]} labels
 * @param {number} copiesPerLabel
 */
export function expandLabelsWithCopies(labels, copiesPerLabel = 1) {
  const n = Math.max(1, Math.min(99, parseInt(String(copiesPerLabel), 10) || 1));
  /** @type {LabelSpec[]} */
  const out = [];
  for (const label of labels) {
    for (let i = 0; i < n; i += 1) {
      out.push(label);
    }
  }
  return out;
}

function normalizeCode(code) {
  const normalized = String(code ?? '').trim().toUpperCase();
  if (!CLKI_CODE_REGEX.test(normalized)) {
    throw new Error('Code interne invalide pour PDF');
  }
  return normalized;
}

/**
 * @param {Document} doc
 */
function renderBarcodePngDataUrl(doc, code, widthPx, heightPx) {
  const normalized = normalizeCode(code);
  const svg = doc.createElementNS('http://www.w3.org/2000/svg', 'svg');
  JsBarcode(svg, normalized, {
    ...BARCODE_PRINT_OPTIONS,
    width: 1.4,
    height: Math.max(28, heightPx * 0.35),
    margin: 8,
  });
  const svgString = new XMLSerializer().serializeToString(svg);
  const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(svgBlob);

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = doc.createElement('canvas');
        canvas.width = widthPx;
        canvas.height = heightPx;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas 2D indisponible'));
          return;
        }
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, widthPx, heightPx);
        ctx.drawImage(img, 0, 0, widthPx, heightPx);
        resolve(canvas.toDataURL('image/png'));
      } catch (err) {
        reject(err);
      } finally {
        URL.revokeObjectURL(url);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Rendu code-barres impossible'));
    };
    img.src = url;
  });
}

function fitLines(pdf, text, maxWidthMm, fontSize) {
  pdf.setFontSize(fontSize);
  const raw = String(text ?? '').trim();
  if (!raw) return [];
  const words = raw.split(/\s+/);
  const lines = [];
  let line = '';
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (pdf.getTextWidth(candidate) <= maxWidthMm) {
      line = candidate;
    } else {
      if (line) lines.push(line);
      line = word;
    }
  }
  if (line) lines.push(line);
  return lines.slice(0, 2);
}

/**
 * @param {object} params
 * @param {LabelSpec[]} params.labels
 * @param {'a4'|'thermal'} [params.format]
 * @param {number} [params.copiesPerLabel]
 * @param {Document} [params.doc]
 */
export async function buildInternalBarcodeLabelsPdf({
  labels,
  format = 'a4',
  copiesPerLabel = 1,
  doc = typeof document !== 'undefined' ? document : null,
}) {
  if (!doc) {
    return { ok: false, error: 'Environnement navigateur requis pour le PDF.' };
  }
  if (!Array.isArray(labels) || labels.length === 0) {
    return { ok: false, error: 'Aucune étiquette à exporter.' };
  }

  const expanded = expandLabelsWithCopies(labels, copiesPerLabel);

  try {
    if (format === 'thermal') {
      const { widthMm, heightMm } = THERMAL_LABEL_LAYOUT;
      const pdf = new jsPDF({ unit: 'mm', format: [widthMm, heightMm], orientation: 'portrait' });
      for (let i = 0; i < expanded.length; i += 1) {
        if (i > 0) pdf.addPage([widthMm, heightMm], 'portrait');
        const label = expanded[i];
        const pad = 2;
        const innerW = widthMm - pad * 2;
        let y = pad + 3;
        pdf.setFont('helvetica', 'bold');
        for (const line of fitLines(pdf, label.title, innerW, 8)) {
          pdf.text(line, widthMm / 2, y, { align: 'center' });
          y += 3.2;
        }
        if (label.variantLabel) {
          pdf.setFont('helvetica', 'normal');
          for (const line of fitLines(pdf, label.variantLabel, innerW, 7)) {
            pdf.text(line, widthMm / 2, y, { align: 'center' });
            y += 2.8;
          }
        }
        if (label.priceLabel) {
          pdf.setFont('helvetica', 'normal');
          pdf.text(String(label.priceLabel), widthMm / 2, y, { align: 'center' });
          y += 3;
        }
        const png = await renderBarcodePngDataUrl(doc, label.code, 320, 80);
        const barH = 10;
        const barW = innerW - 4;
        pdf.addImage(png, 'PNG', pad + 2, y, barW, barH);
        y += barH + 2;
        pdf.setFont('courier', 'bold');
        pdf.setFontSize(7);
        pdf.text(normalizeCode(label.code), widthMm / 2, y, { align: 'center' });
      }
      return { ok: true, pdf, blob: pdf.output('blob') };
    }

    const layout = A4_LABEL_LAYOUT;
    const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
    const usableW = layout.pageWidthMm - layout.marginMm * 2;
    const usableH = layout.pageHeightMm - layout.marginMm * 2;
    const cellW = (usableW - layout.gapMm * (layout.cols - 1)) / layout.cols;
    const cellH = (usableH - layout.gapMm * (layout.rows - 1)) / layout.rows;
    const perPage = layout.cols * layout.rows;

    for (let i = 0; i < expanded.length; i += 1) {
      if (i > 0 && i % perPage === 0) {
        pdf.addPage('a4', 'portrait');
      }
      const indexOnPage = i % perPage;
      const col = indexOnPage % layout.cols;
      const row = Math.floor(indexOnPage / layout.cols);
      const x = layout.marginMm + col * (cellW + layout.gapMm);
      const y = layout.marginMm + row * (cellH + layout.gapMm);
      const label = expanded[i];
      const pad = 2;
      let cy = y + pad + 3;

      pdf.setDrawColor(180);
      pdf.rect(x, y, cellW, cellH);

      pdf.setFont('helvetica', 'bold');
      for (const line of fitLines(pdf, label.title, cellW - pad * 2, 8)) {
        pdf.text(line, x + cellW / 2, cy, { align: 'center' });
        cy += 3.2;
      }
      if (label.variantLabel) {
        pdf.setFont('helvetica', 'normal');
        for (const line of fitLines(pdf, label.variantLabel, cellW - pad * 2, 7)) {
          pdf.text(line, x + cellW / 2, cy, { align: 'center' });
          cy += 2.8;
        }
      }
      if (label.priceLabel) {
        pdf.text(String(label.priceLabel), x + cellW / 2, cy, { align: 'center' });
        cy += 3;
      }

      const png = await renderBarcodePngDataUrl(doc, label.code, 360, 90);
      const barH = Math.min(14, cellH - (cy - y) - 8);
      const barW = cellW - pad * 2 - 2;
      pdf.addImage(png, 'PNG', x + pad + 1, cy, barW, barH);
      cy += barH + 2;
      pdf.setFont('courier', 'bold');
      pdf.setFontSize(7);
      pdf.text(normalizeCode(label.code), x + cellW / 2, cy, { align: 'center' });
    }

    return { ok: true, pdf, blob: pdf.output('blob') };
  } catch (err) {
    return {
      ok: false,
      error: err?.message || 'Échec de génération du PDF.',
    };
  }
}

/**
 * @returns {Promise<{ ok: true, filename: string } | { ok: false, error: string }>}
 */
export async function downloadInternalBarcodeLabelsPdf(params) {
  const built = await buildInternalBarcodeLabelsPdf(params);
  if (!built.ok) return built;
  const format = params.format === 'thermal' ? 'thermique-80x40' : 'a4';
  const filename = `etiquettes-clki-${format}-${new Date().toISOString().slice(0, 10)}.pdf`;
  const url = URL.createObjectURL(built.blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    return { ok: true, filename };
  } finally {
    URL.revokeObjectURL(url);
  }
}
